import dayjs from 'dayjs';

import { beginSubscription, subscriptionCancel, subscriptionChange, subscriptionChangeRenew } from '@/api/apiService';
import PopupCardManage from '@/components/domains/self-service/payment-settings/PopupCardManage.vue';
import PopupCardRegister from '@/components/domains/self-service/payment-settings/PopupCardRegister.vue';
import PopupCreditAutoRecharge from '@/components/domains/self-service/payment-settings/PopupCreditAutoRecharge.vue';
import PopupCreditBuy from '@/components/domains/self-service/payment-settings/PopupCreditBuy.vue';
import PopupPlanUpgrade from '@/components/domains/self-service/payment-settings/PopupPlanUpgrade.vue';
import PopupProUpgrade from '@/components/domains/self-service/payment-settings/PopupProUpgrade.vue';
import PopupResubscription from '@/components/domains/self-service/payment-settings/PopupResubscription.vue';
import PopupStripeCardAdd from '@/components/domains/self-service/payment-settings/PopupStripeCardAdd.vue';
import PopupSubscriptionPayment from '@/components/domains/self-service/payment-settings/PopupSubscriptionPayment.vue';
import PopupMedium from '@/components/shared/composite/PopupMedium.vue';
import PopupSmall from '@/components/shared/composite/PopupSmall.vue';
import { useBrandPay } from '@/composables/domains/self-service/useBrandPay';
import { useGtag } from '@/composables/shared/useGtag';
import { usePaymentStore } from '@/stores/payment';
import { usePopupStore } from '@/stores/popup';
import { useSubscriptionStore } from '@/stores/subscription';
import { useToastStore } from '@/stores/toast';
import { BillingCycleType, PaymentPopupType, PaymentType, TypePlan } from '@/types/components';
import { dateFormatKebab } from '@/utils/dateFormatter';

export const usePayment = () => {
  const { t } = useI18n();

  const subscriptionStore = useSubscriptionStore();
  const paymentStore = usePaymentStore();
  const popupStore = usePopupStore();
  const toastStore = useToastStore();

  const { requestAgreement, isAgreedBilling, getStatusAgreement } = useBrandPay();
  const { trackEventPurchase, trackEventFunnel } = useGtag();

  const { planPrices, paymentType, currentPlan, userPlan, currentPlanDetails, currentCredits, currentPlanStatus } =
    storeToRefs(subscriptionStore);

  // ========================
  // 결제 action
  // ========================

  const hasRegisteredCard = () => {
    return Object.values(paymentStore.cardList).some((card) => card !== null);
  };

  const refetchUserSubscriptionInfo = async () => {
    await paymentStore.fetchUserCardList();
    await subscriptionStore.fetchSubscriptionInfo();
  };

  const handleSubscriptionPayment = async (plan: TypePlan, billingCycle: BillingCycleType, price: number = 0) => {
    try {
      const isAgreed = await checkPaymentAgreement();
      if (!isAgreed) {
        return;
      }

      const isResubscription = currentPlan.value === plan && currentPlanDetails.value?.billingCycle === billingCycle;
      const param = setPaymentParameter(plan, billingCycle);

      const subscriptionFn = currentPlan.value === 'FREE' || isResubscription ? beginSubscription : subscriptionChange;
      const { success, data } = await subscriptionFn(param);

      if (!success) {
        throw new Error();
      }

      trackEventFunnel(price > 0 ? 'complete_subscription' : 'complete_resubscription');
      trackEventPurchase(price, subscriptionStore.currency, data.id);
    } catch (error) {
      console.error('구독 결제 실패: ', error);
    } finally {
      await subscriptionStore.fetchSubscriptionInfo();
      popupStore.closeLast();
    }
  };

  const checkPaymentAgreement = async (): Promise<boolean> => {
    if (paymentType.value === 'TOSS') {
      await getStatusAgreement();

      if (!isAgreedBilling.value) {
        return new Promise<boolean>((resolve) => {
          requestAgreement(
            () => resolve(true),
            () => resolve(false)
          );
        });
      }
    }

    return true;
  };

  const setPaymentParameter = (plan, billingCycle) => {
    const {
      rechargeAllow = false,
      rechargeBaseAmount = null,
      rechargeUnitAmount = null,
      rechargeLimitAmount = null,
    } = currentPlan.value === 'PRO' && plan === 'PRO' ? currentPlanDetails.value! : {};

    return {
      plan,
      billingCycle,
      rechargeAllow,
      rechargeBaseAmount,
      rechargeUnitAmount,
      rechargeLimitAmount,
      paymentType: paymentType.value,
    };
  };

  const cancelAutoRecharge = async () => {
    trackEventFunnel('click_auto_recharge_cancel_button');

    const { id, plan, paymentType, rechargeAllow } = currentPlanDetails.value!;
    const param = {
      plan,
      paymentType,
      rechargeAllow: !rechargeAllow,
      rechargeBaseAmount: null,
      rechargeUnitAmount: null,
      rechargeLimitAmount: null,
    };

    const { success } = await subscriptionChangeRenew(id, param);
    if (!success) {
      return;
    }

    trackEventFunnel('complete_auto_recharge_cancel');

    await subscriptionStore.fetchSubscriptionInfo();
    toastStore.info(t('auto_refill_success_deactivate'));
  };

  const handleSubscriptionCancel = () => {
    if (currentPlanStatus.value === 'PLANNED') {
      popupStore.open(PopupSmall, {
        title: t('confirm_cancel_subscription'),
        subTitle: t('confirm_cancel_from_downgrade', {
          planToBe: subscriptionStore.plannedPlanDetails?.plan,
          planAsIs: subscriptionStore.currentPlan,
          endDate: dateFormatKebab(subscriptionStore.currentPlanDetails!.endDate),
        }),
        cancelButton: { text: t('label_cancel') },
        confirmButton: { text: t('confirm'), action: () => cancelSubscription() },
      });
      return;
    }

    popupStore.open(PopupSmall, {
      title: t('confirm_cancel_subscription'),
      subTitle: `${t('modal_subscription_cancel_body', {
        date: dayjs(subscriptionStore.currentPlanDetails!.nextPaymentDate).format('YYYY-MM-DD'),
      })} \n ${t('modal_subscription_cancel_body_2')}`,
      cancelButton: { text: t('label_cancel') },
      confirmButton: { text: t('confirm'), action: () => cancelSubscription() },
    });
  };

  const cancelSubscription = async () => {
    const { success } = await subscriptionCancel();
    if (success) {
      await subscriptionStore.fetchSubscriptionInfo();
    }
  };

  // ========================
  // 팝업 action
  // ========================
  const openCardRegisterPopup = (): Promise<PaymentType | null> => {
    trackEventFunnel('open_card_register_popup');

    return new Promise((resolve) => {
      const popupId = popupStore.open(PopupSmall, {
        title: t('self_service_register_card'),
        contentSlot: h(PopupCardRegister, {
          close: () => {
            popupStore.close(popupId);
            resolve(null);
          },
          callback: async (cardType?: PaymentType) => {
            await refetchUserSubscriptionInfo();
            popupStore.close(popupId);

            resolve(cardType ?? null);
          },
        }),
        showCloseButton: true,
      });
    });
  };

  const openStripeCardAddPopup = () => {
    return new Promise((resolve) => {
      const popupId = popupStore.open(PopupSmall, {
        title: t('card_manage_register_foreign'),
        contentSlot: h(PopupStripeCardAdd, {
          callback: async () => {
            await refetchUserSubscriptionInfo();
            popupStore.close(popupId);

            resolve(true);
          },
          close: () => popupStore.close(popupId),
        }),
        showCloseButton: true,
      });
    });
  };

  const openCardManagePopup = (
    popupMode: PaymentPopupType = 'MANAGE',
    cardType: PaymentType | null
  ): Promise<PaymentType | undefined> => {
    if (popupMode === 'PAYMENT') {
      trackEventFunnel('open_card_select_popup');
    }

    return new Promise((resolve) => {
      const popupId = popupStore.open(PopupSmall, {
        title: t('self_service_manage_card'),
        contentSlot: h(PopupCardManage, {
          initialTab: cardType,
          popupMode,
          callback: (cardType?: PaymentType) => {
            if (popupMode === 'PAYMENT') {
              popupStore.close(popupId);
            }

            resolve(cardType);
          },
        }),
        showCloseButton: true,
      });
    });
  };

  const openSubscriptionPaymentPopup = (plan: TypePlan, billingCycle = 'MONTHLY') => {
    trackEventFunnel('open_subscription_payment_popup');

    popupStore.open(PopupSmall, {
      title: t('self_service_subscribe'),
      contentSlot: h(PopupSubscriptionPayment),
      payload: {
        plan,
        billingCycle,
        price: planPrices.value[plan],
      },
      showCloseButton: true,
    });
  };

  const openResubscriptionPopup = (billingCycle) => {
    trackEventFunnel('open_resubscription_popup');

    popupStore.open(PopupSmall, {
      title: t('resume_subscription'),
      contentSlot: h(PopupResubscription, {
        plan: userPlan.value,
      }),
      cancelButton: { text: t('label_cancel') },
      confirmButton: {
        text: t('label_resubscription'),
        action: () => handleSubscriptionPayment(currentPlan.value, billingCycle),
      },
    });
  };

  const openPlanUpgradePopup = () => {
    trackEventFunnel('open_plan_upgrade_popup');

    popupStore.open(PopupSmall, {
      title: currentCredits.value > 0 ? t('upgrade') : t('error_no_credit_modal_title'),
      contentSlot: h(PopupPlanUpgrade),
      showCloseButton: true,
    });
  };

  const openProUpgradePopup = (callback: () => void) => {
    trackEventFunnel('open_pro_upgrade_popup');

    popupStore.open(PopupSmall, {
      contentSlot: h(PopupProUpgrade),
      cancelButton: { text: t('do-it-later') },
      confirmButton: { text: t('ss_upsell_updgrade_to', { planAfter: 'Pro' }), action: () => callback() },
    });
  };

  const openCreditAutoRechargePopup = () => {
    trackEventFunnel('open_auto_recharge_setting_popup');

    popupStore.open(PopupSmall, {
      contentSlot: h(PopupCreditAutoRecharge),
    });
  };

  const openCreditBuyPopup = () => {
    trackEventFunnel('open_credit_buy_popup');

    popupStore.open(PopupMedium, {
      title: t('buy_credits'),
      contentSlot: h(PopupCreditBuy),
      showCloseButton: true,
    });
  };

  return {
    // 결제 action
    hasRegisteredCard,
    handleSubscriptionPayment,
    checkPaymentAgreement,
    cancelAutoRecharge,
    handleSubscriptionCancel,
    refetchUserSubscriptionInfo,
    // 팝업 action
    openCardRegisterPopup,
    openStripeCardAddPopup,
    openCardManagePopup,
    openSubscriptionPaymentPopup,
    openResubscriptionPopup,
    openProUpgradePopup,
    openPlanUpgradePopup,
    openCreditAutoRechargePopup,
    openCreditBuyPopup,
  };
};
